'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { IUser } from '@ziro/legal-shared-types';
import { UserRole, PracticeArea, ComplianceRole } from '@ziro/legal-shared-types';
import { getSession, clearSession, revokeServerSession, type AuthSession } from './session';
import { navigateTo } from '@/lib/navigation';
import { clearAllDrafts, clearOtherUsersDrafts } from '@/components/cases/checkpoints/ai-fill/unit-edit-draft';

interface AuthContextValue {
  user: IUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (returnTo?: string) => void;
  /** Explicit Sign out: revokes every session server-side, then clears this browser. */
  logout: () => Promise<void>;
  /**
   * Clears THIS browser's session only — no server call, no redirect (the
   * caller navigates). For the idle timeout: walking away from one tab must
   * not sign the person out of every other device (2026-10-06 follow-up to
   * audit2 §22).
   */
  clearLocalSession: () => void;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<IUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const session = getSession();
    if (session) {
      setUser(sessionToUser(session));
      // Unsaved AI-section drafts are per user: another person's never stays in this tab.
      clearOtherUsersDrafts(session.user.id);
    }
    setIsLoading(false);
  }, []);

  /**
   * Spec §2.1 — API-driven for BOTH providers.
   *
   * This used to build a `login.microsoftonline.com/.../authorize` URL in
   * the browser from `NEXT_PUBLIC_AZURE_AD_TENANT_ID` / `_CLIENT_ID`,
   * which is wrong three times over on a multi-firm tier: those are
   * BUILD-TIME constants in a per-tier image, so every firm got firm #1's
   * tenant; the `redirect_uri` was derived client-side, bypassing A8's
   * per-firm derivation; and a Google firm had no path at all.
   * `GET /api/auth/login` already resolves the provider from the firm this
   * host belongs to, mints the CSRF `state` server-side and derives the
   * redirect from the request. A relative URL is deliberate — the browser
   * is already on the firm's host and nginx path-routes `/api/`.
   */
  const login = useCallback((returnTo?: string) => {
    const isMockAuth = process.env.NEXT_PUBLIC_MOCK_AUTH === 'true';
    if (isMockAuth) {
      navigateTo('/login');
      return;
    }

    const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    navigateTo(`/api/auth/login${query}`);
  }, []);

  /**
   * audit2 §22, 2026-10-06 — sign out on the SERVER first, then locally.
   * This used to clear only local state, so the bearer JWT in the
   * `cl_auth_token` cookie kept verifying until `exp` (90 days on dev).
   * `POST /api/auth/logout` now revokes every live token for this person
   * (`sessionsRevokedAt`) and clears both cookies. The request is SENT
   * before local state is cleared (it carries the token as a Bearer header,
   * read first), then local state is cleared synchronously — callers such as
   * a caller may navigate away straight after calling this, so nothing
   * local may wait on the network — and the redirect waits for the API,
   * capped, so a hung API can't trap the user. A failure still signs out
   * locally. `keepalive` lets the request outlive a navigation.
   */
  const logout = useCallback(async () => {
    const revoked = revokeServerSession(getSession()?.accessToken ?? null);
    // An explicit Sign out drops unsaved AI-section drafts. A session that merely
    // EXPIRED (a 401 → clearSession) keeps them: re-login as the same user restores them.
    clearAllDrafts();
    clearSession();
    setUser(null);
    await revoked;
    navigateTo('/login');
  }, []);

  /**
   * Local-only sign-out. Deliberately does NOT call `revokeServerSession`:
   * `POST /api/auth/logout` revokes every live token for the person
   * (`sessionsRevokedAt`), which is right for an explicit Sign out and wrong
   * for an idle timeout in one tab — that would sign them out on every other
   * device too. Clearing the stored session and the `cl_auth_token` cookie is
   * enough to end this browser's access.
   */
  const clearLocalSession = useCallback(() => {
    // Idle timeout: walking away ends the session and its unsaved drafts.
    clearAllDrafts();
    clearSession();
    setUser(null);
  }, []);

  const getToken = useCallback(() => {
    const session = getSession();
    return session?.accessToken ?? null;
  }, []);

  // Stabilise the context value across renders so consumers that depend on
  // it inside useEffect deps don't keep firing — without useMemo the object
  // literal is a fresh reference on every render and can fan out into
  // React #185 ("Maximum update depth") loops in downstream effects.
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      clearLocalSession,
      getAccessToken: getToken,
    }),
    [user, isLoading, login, logout, clearLocalSession, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const defaultAuthValue: AuthContextValue = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: () => {},
  logout: async () => {},
  clearLocalSession: () => {},
  getAccessToken: () => null,
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  return context ?? defaultAuthValue;
}

function sessionToUser(session: AuthSession): IUser {
  return {
    id: session.user.id,
    externalIdentityId: '',
    email: session.user.email,
    displayName: session.user.displayName,
    role: session.user.role as UserRole,
    practiceAreas: session.user.practiceAreas as PracticeArea[],
    complianceRoles: session.user.complianceRoles as ComplianceRole[] | undefined,
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}
